import { OrderPaymentStatus } from '@/api/common-types'
import { OrderInitiatedPaymentInfoBody, PaymentInfoTransaction } from '@/api/orders-types'
import { useApiContext, useGetOrderPaymentInfo } from '@/api/react-query'
import { PaymentProvider, PaymentProviderType, TransactionStatus } from '@/api/store-orders-types'
import { Sentry } from '@/utils/error-handling'
import { useAsyncEffect, useCallbackRef, useGetSubject, useSubject } from '@/utils/hooks'
import { StackBehaviourSubject } from '@/utils/rx-utilities'
import { computePaymentState, isInitiationTimedOut, PaymentState } from './payment-state'
import { useCallback, useEffect, useMemo } from 'react'
import { BehaviorSubject } from 'rxjs'
import { ulid } from 'ulid'

export { PaymentState }

export type TransactionIdentifier = {
	orderUuid: string
	transactionReference: string
	initiationAttemptedAt: number
}
export const usePaymentHelper = ({
	orderUuid,
	transactionIdentifierSubject,
	onProviderDataReady,
	onError,
	onCompleted
}: {
	orderUuid: string
	transactionIdentifierSubject: BehaviorSubject<TransactionIdentifier | null>
	onProviderDataReady: (transaction: PaymentInfoTransaction) => void
	onError: (message: string) => void
	onCompleted: () => void
}) => {
	const [paymentStarted, paymentStartedSubject] = useSubject(false)
	const [isInitiating, isInitiatingSubject] = useSubject(false)

	const [isCancelling, isCancellingSubject] = useSubject(false)

	const onCompletedRef = useCallbackRef(onCompleted)
	const onErrorRef = useCallbackRef(onError)
	const onProviderDataReadyRef = useCallbackRef(onProviderDataReady)

	const transactionIdentifier = useGetSubject(transactionIdentifierSubject)

	const { api } = useApiContext()

	const waitingForProviderData = useMemo<Record<PaymentProviderType, StackBehaviourSubject<boolean>>>(
		() =>
			Object.fromEntries(
				Object.values(PaymentProvider).map(provider => [provider, new StackBehaviourSubject(false)])
			) as Record<PaymentProviderType, StackBehaviourSubject<boolean>>,
		[]
	)

	const isPolling = !!orderUuid && !!transactionIdentifier?.transactionReference && paymentStarted
	const {
		data: pollOrderPaymentInfo,
		error: pollOrderPaymentInfoError,
		dataUpdatedAt
	} = useGetOrderPaymentInfo(
		{ orderUuid },
		{
			staleTime: 1,
			refetchOnMount: true,
			refetchInterval: isPolling ? 1000 : 0,
			enabled: isPolling && !!orderUuid
		}
	)

	// Order mismatch check
	useEffect(() => {
		if (transactionIdentifier?.orderUuid && transactionIdentifier?.orderUuid !== orderUuid) {
			transactionIdentifierSubject.next(null)
			return
		}
	}, [transactionIdentifier?.orderUuid, orderUuid])

	useAsyncEffect(async () => {
		try {
			console.log('Checking order payment info', orderUuid)
			const orderPaymentInfo = await api.getOrderPaymentInfo({ path: { orderUuid } })
			console.log('Order payment info check', orderPaymentInfo)
			const pendingTransaction = orderPaymentInfo?.transactions?.find(
				t =>
					t.transactionStatus === TransactionStatus.PendingAuth ||
					t.transactionStatus === TransactionStatus.PendingCancel
			)
			console.log('Pending transaction', pendingTransaction)
			if (pendingTransaction) {
				console.log('Pending transaction found, storing client reference for polling')

				paymentStartedSubject.next(true)
				Object.values(waitingForProviderData).forEach(s => s.nextIfDifferent(false))
				if (pendingTransaction.transactionStatus === TransactionStatus.PendingAuth) {
					waitingForProviderData[pendingTransaction.provider].next(true)
				} else if (pendingTransaction.transactionStatus === TransactionStatus.PendingCancel) {
					isCancellingSubject.next(true)
				}
				transactionIdentifierSubject.next({
					orderUuid,
					transactionReference: pendingTransaction.clientReference,
					initiationAttemptedAt: Date.now()
				})
			}
		} catch (error) {
			console.error('Error checking order payment info', error)
			throw error
		}
	}, [])

	useEffect(() => {
		console.log('Checking pollOrderPaymentInfo', pollOrderPaymentInfo)
		if (!paymentStartedSubject.value) {
			console.log('Payment not started, returning')
			return
		}
		if (pollOrderPaymentInfo?.status === OrderPaymentStatus.Paid) {
			console.log('Payment completed')
			transactionIdentifierSubject.next(null)
			onCompletedRef()
			return
		}
		if (!transactionIdentifier) {
			console.log('No transaction identifier')
			return
		}

		if (transactionIdentifier && transactionIdentifier?.orderUuid !== orderUuid) {
			console.log('Transaction identifier order id mismatch, clearing transaction identifier')
			transactionIdentifierSubject.next(null)
			return
		}
		const transactionReference = transactionIdentifier.transactionReference
		console.log('Finding transaction', {
			transactionReference,
			transactions: pollOrderPaymentInfo?.transactions
		})
		const transaction = pollOrderPaymentInfo?.transactions.find(t => t.clientReference === transactionReference)
		console.log('Transaction found', transaction)
		if (!transaction) {
			console.log('No transaction found, checking timeout')
			if (isInitiationTimedOut(transactionIdentifier?.initiationAttemptedAt, Date.now())) {
				console.log('Payment initiation timed out. Clearing transaction reference.', {
					transactionReference: transactionIdentifier.transactionReference,
					orderUuid
				})
				transactionIdentifierSubject.next(null)
				onErrorRef('Payment initiation timed out')
			}
			console.log('No timeout, returning')
			return
		}
		console.log('Transaction found, checking status', transaction.transactionStatus)

		if (isCancellingSubject.value) {
			switch (transaction?.transactionStatus) {
				case TransactionStatus.PendingCancel:
				case TransactionStatus.PendingAuth:
					return
				default:
					console.log('Need to update isCancelling status', transaction.transactionStatus)
					isCancellingSubject.next(false)
					break
			}
		}

		const markTransactionAsTerminal = () => {
			Object.values(waitingForProviderData).forEach(s => s.nextIfDifferent(false))
			paymentStartedSubject.next(false)
			isInitiatingSubject.next(false)
			isCancellingSubject.next(false)
			transactionIdentifierSubject.next(null)
		}

		switch (transaction.transactionStatus) {
			case TransactionStatus.PendingAuth: {
				console.log('Pending auth')
				switch (transaction.provider) {
					case PaymentProvider.Cash:
						break
					case PaymentProvider.AdyenEcom:
						console.log('Adyen ecom', transaction.adyenData)
						if (!transaction.adyenData) {
							console.log('No adyen data, returning')
							return
						}
						if (!waitingForProviderData[PaymentProvider.AdyenEcom].value) {
							console.log('Not waiting for adyen data, returning')
							return
						}
						console.log('Nexting adyen ecom')
						waitingForProviderData[PaymentProvider.AdyenEcom].next(false)
						onProviderDataReadyRef(transaction)
						break
					case PaymentProvider.VerifoneCheckout:
						if (transaction.verifoneData) {
							console.log('Nexting verifone checkout')
							waitingForProviderData[PaymentProvider.VerifoneCheckout].next(false)
							onProviderDataReadyRef(transaction)
						}
						break
					case PaymentProvider.VerifoneEcom:
						if (transaction.verifoneData) {
							console.log('Nexting verifone ecom')
							waitingForProviderData[PaymentProvider.VerifoneEcom].next(false)
							onProviderDataReadyRef(transaction)
						}
						break
					default:
						throw new Error(`Unsupported payment provider: ${(transaction as any).provider}`)
				}
				break
			}
			case TransactionStatus.PendingCancel:
				break
			case TransactionStatus.Completed:
				console.log('Transaction completed. Clearing transaction reference.', {
					transactionReference,
					orderUuid
				})
				markTransactionAsTerminal()
				onCompletedRef()
				break
			case TransactionStatus.Failed:
				console.log('Transaction failed. Clearing transaction reference.', {
					transactionReference,
					orderUuid
				})
				markTransactionAsTerminal()
				onErrorRef('Payment failed')
				break
			case TransactionStatus.Canceled:
				console.log('Transaction canceled. Clearing transaction reference.', {
					transactionReference,
					orderUuid
				})
				markTransactionAsTerminal()
				onErrorRef('Payment successfully cancelled')
				break
			case TransactionStatus.Expired:
				console.log('Transaction expired. Clearing transaction reference.', {
					transactionReference,
					orderUuid
				})
				markTransactionAsTerminal()
				onErrorRef('Payment expired')
				break
			default:
				console.log('Unexpected transaction status. Clearing transaction reference.', {
					transactionReference,
					orderUuid
				})
				markTransactionAsTerminal()
				onErrorRef('Payment error')
				break
		}
	}, [pollOrderPaymentInfo, transactionIdentifier, dataUpdatedAt])

	const initiatePayment = useCallback(
		async (data: OrderInitiatedPaymentInfoBody) => {
			if (isInitiatingSubject.value) {
				// Sanity check, should never happen
				return
			}
			try {
				isInitiatingSubject.next(true)
				paymentStartedSubject.next(true)
				Object.values(waitingForProviderData).forEach(s => s.nextIfDifferent(false))
				waitingForProviderData[data.provider].nextIfDifferent(true)

				if (transactionIdentifierSubject.value) {
					console.log('Transaction identifier already set, no need to create a new one')
					return
				}

				const orderPaymentInfo = await api.getOrderPaymentInfo({ path: { orderUuid } })
				console.log('Order payment info', orderPaymentInfo)
				const pendingTransaction = orderPaymentInfo?.transactions.find(
					t => t.provider === data.provider && t.transactionStatus === TransactionStatus.PendingAuth
				)
				console.log('Pending transaction', pendingTransaction)
				if (pendingTransaction) {
					console.log('Pending transaction found, storing client reference for polling')

					transactionIdentifierSubject.next({
						orderUuid,
						transactionReference: pendingTransaction.clientReference,
						initiationAttemptedAt: Date.now()
					})
					return
				}

				const { clientReference } = data
				await api.initiateOrderPayment({
					headers: {
						'x-idempotency-key': clientReference
					},
					path: {
						orderUuid
					},
					body: data
				})

				// Store clientReference for polling
				transactionIdentifierSubject.next({
					orderUuid,
					transactionReference: clientReference,
					initiationAttemptedAt: Date.now()
				})
			} catch (e) {
				onErrorRef('Payment initiation failed')
				console.error('onPay error', e)
				Sentry.captureException(e)
			} finally {
				isInitiatingSubject.next(false)
			}
		},
		[orderUuid]
	)

	const cancelPayment = useCallback(async () => {
		if (!transactionIdentifier) {
			return
		}
		try {
			isCancellingSubject.next(true)
			const cancelPaymentResponse = await api.cancelOrderPaymentNoThrow({
				headers: {
					'x-idempotency-key': ulid()
				},
				path: { orderUuid: transactionIdentifier.orderUuid },
				body: {
					clientReference: transactionIdentifier.transactionReference
				}
			})
			if (!cancelPaymentResponse.isSuccess) {
				isCancellingSubject.next(false)
				throw new Error('Failed to cancel payment')
			}
		} catch (error) {
			// TODO
			// Sentry.captureException(error)
			isCancellingSubject.next(false)
			console.error('Payment cancellation failed:', error)
			onError('Payment cancellation failed. Please try again.')
		}
	}, [transactionIdentifier])

	const paymentState = useMemo(
		() =>
			computePaymentState({
				isCancelling,
				paymentStarted,
				isInitiating,
				isWaitingForProviderData: Object.values(waitingForProviderData).some(s => s.value)
			}),
		[paymentStarted, isInitiating, transactionIdentifier, isCancelling]
	)

	const updateTransactionState = useCallback(
		(state: 'COMPLETED' | 'FAILED' | 'CANCELLED') => {
			Object.values(waitingForProviderData).forEach(s => s.next(false))
			switch (state) {
				case 'COMPLETED':
					break
				case 'CANCELLED':
					paymentStartedSubject.next(false)
					break
				case 'FAILED':
					paymentStartedSubject.next(false)
					transactionIdentifierSubject.next(null)
					break
			}
		},
		[waitingForProviderData]
	)

	return {
		paymentState,
		initiatePayment,
		updateTransactionState,
		cancelPayment
	}
}
